import { createFileRoute, redirect } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "@/components/common/Buttons/Button";
import Popup from "@/components/common/Popups/Popup";
import DeviceImage from "@/components/offer/DeviceImage";
import PriceSection from "@/components/offer/PriceSection";
import useNavigationHelpers from "@/hooks/useNavigationHelpers";
import { useStore } from "@/store";

export const Route = createFileRoute("/$locale/$partner/_app/_card/max-price")({
	// Step order is derived from the URL alone, so browser-back, a deep link or a restored
	// route can land here with no identification — which pricing rejects, stranding the
	// user on a generic error. The scan step is the recoverable destination.
	beforeLoad: ({ params, search }) => {
		if (useStore.getState().imeiInfo?.identification?.id) return;

		throw redirect({
			to: "/$locale/$partner/imei-scan",
			params: { locale: params.locale, partner: params.partner },
			search,
			replace: true,
		});
	},
	component: MaxPrice,
});

function MaxPrice() {
	const { t } = useTranslation();
	const { goToNextStep, goToFirstPage } = useNavigationHelpers();
	const deviceInfo = useStore((state) => state.imeiInfo?.identification.extracted_metadata);
	const [exitPopup, _setExitPopup] = useState<boolean>(false);

	return (
		<div className="flex flex-col items-center h-full grow relative">
			<h5 className=" border-b border-[#BFBFBF] text-center w-full py-2 sm:py-4">
				{deviceInfo?.brand} {deviceInfo?.model} {deviceInfo?.capacity && "•"} {deviceInfo?.capacity}
			</h5>
			<div className="flex flex-col items-center justify-center h-full w-full gap-y-8 md:gap-y-16 overflow-y-auto overflow-x-hidden">
				<h4 className="font-semibold text-center">{t("instructions.Your device’s top trade-in value potential is")}</h4>
				<PriceSection isMaxPrice />
				{deviceInfo && (
					<div className="h-[40%] w-full relative">
						<DeviceImage
							category={"smartphone"}
							brand={deviceInfo?.brand}
							model={deviceInfo?.model}
						/>
					</div>
				)}
			</div>
			<h5 className="font-bold mb-4 text-center">
				{t("instructions.Let’s find out what it’s worth in less than a 3 mins right now!")}
			</h5>
			<div className="sticky bottom-0 bg-fixed-buttons w-full flex flex-col-reverse sm:flex-row flex-wrap items-center justify-center mt-auto sm:gap-2 lg:gap-4 pt-4">
				{/* <Button intent={"quaternary"} label={t("btn.Not interested")} onClick={() => setExitPopup(true)} /> */}
				<Button
					intent={"primary"}
					label={t("btn.Start evaluation")}
					onClick={() => goToNextStep()}
				/>
			</div>
			<Popup
				show={exitPopup}
				primaryButtonLabel={t("btn.No")}
				primaryButtonOnClick={() => goToNextStep()}
				secondaryButtonLabel={t("btn.Yes")}
				secondaryButtonOnClick={() => goToFirstPage()}
				content={
					<div className="flex flex-col items-center text-center gap-y-4 max-w-lg pt-8">
						<h3>{t("popup.Trade-in now to secure the best value for your device.")}</h3>
						<h4>{t("popup.Are you sure you want to exit?")}</h4>
					</div>
				}
			/>
		</div>
	);
}
